// Importacion de rideRequests from planillas (R1.5 Fase A, pieza 2).
// La site real lleva su padron y sus pedidos en Excel y WhatsApp; el
// formato exacto se conoce recien en la semana 1. Por eso el contrato es un
// MAPEO DE COLUMNAS configurable (con alias por defecto en castellano) y un
// resumen tolerante: la planilla nunca se rechaza entera por una fila mala,
// cada rechazo dice fila, columna y reason.
//
// Este contrato envuelve al de import por lote JSON (rideRequests.ts):
// la planilla se convierte a filas ImportedRideRequest y el resultado de esa
// import se devuelve tal cual dentro de `import`.
import { z } from "zod";
import { OperatingDate, ImportRideRequestsResponse, RideRequestDirection } from "./ride-requests.js";
/**
 * Hora local de la site en formato HH:MM (24 h). Definida aca hasta que
 * comunes.ts publique LocalTime (pieza 1 de la Fase A); el orquestador la
 * reemplaza por esa definicion unica cuando exista.
 */
export const SpreadsheetTime = z
    .string()
    .regex(/^([01]\d|2[0-3]):[0-5]\d$/, "hora HH:MM (24 h)")
    .openapi("SpreadsheetTime", {
    description: "Hora local de la planta en formato HH:MM (24 h).",
    example: "06:00",
});
/** Nombre de columna tal como aparece en la fila de encabezados de la planilla. */
const ColumnHeaderName = z.string().trim().min(1).max(120);
export const ColumnMappingRequest = z
    .object({
    record: ColumnHeaderName.optional().openapi({
        description: "Columna con el legajo del pasajero. Por defecto se buscan alias como legajo, nro legajo, dni, documento, empleado.",
    }),
    direction: ColumnHeaderName.optional().openapi({
        description: "Columna con el direction (ingreso o egreso). Alias por defecto: direction, tipo, movimiento. Los valores admitidos se amplian con inboundValues y outboundValues.",
    }),
    date: ColumnHeaderName.optional().openapi({
        description: "Columna con la fecha operativa del turno, en DD/MM/YYYY o YYYY-MM-DD. Alias por defecto: fecha, dia, fecha turno.",
    }),
    time: ColumnHeaderName.optional().openapi({
        description: "Columna con la hora local del turno (HH:MM). Alias por defecto: hora, horario, hora turno. Se combina con la fecha en la zona horaria de la planta.",
    }),
    instant: ColumnHeaderName.optional().openapi({
        description: "Columna con fecha y hora juntas (DD/MM/YYYY HH:MM, YYYY-MM-DD HH:MM o ISO 8601 UTC). Reemplaza a fecha y hora cuando esta presente. Alias por defecto: instante, fecha hora, timestamp.",
    }),
    originLat: ColumnHeaderName.optional().openapi({
        description: "Columna con la latitud del origin (o destino en egreso). Alias por defecto: lat, latitud.",
    }),
    originLng: ColumnHeaderName.optional().openapi({
        description: "Columna con la longitud del origin. Alias por defecto: lng, lon, longitud.",
    }),
    address: ColumnHeaderName.optional().openapi({
        description: "Columna con la direccion postal del domicilio (calle, numero, localidad). Con la geocodificacion encendida (ADR-022), una fila con direccion y sin coordenadas ni parada se geocodifica desde la API con Google Geocoding (solo la direccion, sin ningun dato de la persona); apagada, se rechaza con motivo address_without_coordinates. Desde 0.6.41 (PP-69), el texto tal cual vino (nunca el de Google) se guarda como domicilio escrito del legajo cuando el punto de la fila (geocodificado o cargado) es su domicilio; las filas de un mismo legajo que no coinciden no lo tocan. Alias por defecto: direccion, domicilio.",
    }),
    stop: ColumnHeaderName.optional().openapi({
        description: "Columna con el id de la parada fija. Alias por defecto: parada, parada id, punto.",
    }),
    sheet: ColumnHeaderName.optional().openapi({
        description: "Solo XLSX: nombre de la hoja a leer. Por defecto, la primera hoja del libro.",
    }),
    inboundValues: z.array(z.string().trim().min(1)).max(20).optional().openapi({
        description: "Valores adicionales de la columna direction que significan ingreso (ademas de ingreso, entrada, ida, subida).",
    }),
    outboundValues: z.array(z.string().trim().min(1)).max(20).optional().openapi({
        description: "Valores adicionales de la columna direction que significan egreso (ademas de egreso, salida, vuelta, regreso).",
    }),
    defaultDate: OperatingDate.optional().openapi({
        description: "Fecha operativa a usar cuando la planilla no trae columna de fecha (una planilla por dia, tipico de listas de WhatsApp).",
    }),
    defaultTime: SpreadsheetTime.optional().openapi({
        description: "Hora local del turno a usar cuando la planilla no trae columna de hora.",
    }),
    defaultDirection: RideRequestDirection.optional().openapi({
        description: "Sentido a usar cuando la planilla no trae columna de direction (una planilla por turno).",
    }),
})
    .openapi("ColumnMappingRequest", {
    description: "Mapeo de columnas de la planilla a los campos de la solicitud. Todo es opcional: sin mapeo, el importador reconoce los alias por defecto en castellano (insensible a mayusculas y acentos).",
});
export const SpreadsheetFormat = z.enum(["csv", "xlsx"]).openapi("SpreadsheetFormat", {
    description: "Formato detectado de la planilla recibida.",
});
export const SpreadsheetRejectionReason = z
    .enum([
    "empty_record",
    "invalid_direction",
    "invalid_date",
    "invalid_time",
    "invalid_instant",
    "invalid_coordinates",
    "address_without_coordinates",
    "missing_origin",
    "ambiguous_origin",
    // ADR-022: geocodificacion de domicilios con Google Geocoding.
    "address_imprecise",
    "address_not_found",
    "geocoding_unavailable",
])
    .openapi("SpreadsheetRejectionReason", {
    description: "Motivo por el que una fila de la planilla no se convirtio en solicitud. address_without_coordinates: la fila trae direccion postal pero no coordenadas ni parada, y la geocodificacion esta apagada en el ambiente. missing_origin: sin coordenadas, parada ni direccion. ambiguous_origin: coordenadas y parada a la vez. address_imprecise (ADR-022): Google ubica la direccion solo de forma aproximada (ni ROOFTOP ni RANGE_INTERPOLATED), devuelve mas de un candidato o una coincidencia parcial; la planta la corrige a mano. address_not_found: Google no encuentra la direccion. geocoding_unavailable: el servicio no respondio, se agoto la cuota diaria o el tiempo de la importacion; se reintenta mas tarde. El detalle nunca incluye la direccion.",
});
export const SpreadsheetRowRejection = z
    .object({
    row: z.number().int().min(2).openapi({
        description: "Numero de fila en la planilla (base 1, la fila 1 es el encabezado), para que la planta la ubique.",
    }),
    column: z.string().optional().openapi({
        description: "Columna de la planilla involucrada, cuando el motivo refiere a una.",
    }),
    reason: SpreadsheetRejectionReason,
    detail: z.string().openapi({
        description: "Explicacion legible con el valor rechazado cuando no es un dato personal (fechas, horas, sentidos, coordenadas). Nunca incluye direcciones.",
    }),
})
    .openapi("SpreadsheetRowRejection", { description: "Una fila de la planilla que no se convirtio, con su motivo." });
export const RejectionCountByReason = z
    .object({
    reason: SpreadsheetRejectionReason,
    count: z.number().int().min(1),
})
    .openapi("RejectionCountByReason", { description: "Cantidad de filas rechazadas por un motivo." });
export const SpreadsheetSummary = z
    .object({
    rowsRead: z.number().int().min(0).openapi({
        description: "Filas con datos leidas de la planilla (sin contar el encabezado ni filas vacias).",
    }),
    converted: z.number().int().min(0).openapi({
        description: "Filas convertidas en solicitudes y enviadas a la importacion.",
    }),
    rejected: z.number().int().min(0).openapi({
        description: "Filas que no se convirtieron; el detalle esta en rejections.",
    }),
})
    .openapi("SpreadsheetSummary", { description: "Resumen de la conversion de la planilla." });
export const ImportSpreadsheetResponse = z
    .object({
    format: SpreadsheetFormat,
    columns: z.array(z.string()).openapi({
        description: "Encabezados leidos de la planilla, en su orden, para diagnosticar el mapeo.",
    }),
    summary: SpreadsheetSummary,
    rejectionsByReason: z.array(RejectionCountByReason),
    rejections: z.array(SpreadsheetRowRejection),
    import: ImportRideRequestsResponse.openapi({
        description: "Resultado de importar las filas convertidas, con la misma forma que POST /ride-requests/import. Sus indices refieren al lote de filas convertidas, en el orden de la planilla; cada fila lleva su requestId derivado (imp_<planta>_<fecha>_<legajo>_<sentido>), estable entre corridas.",
    }),
})
    .openapi("ImportSpreadsheetResponse", {
    description: "Resultado de importar una planilla (CSV o XLSX): resumen de lectura y conversion, rechazos por fila y resultado de la importacion de las filas convertidas.",
});
//# sourceMappingURL=import.js.map